import ArrowDownward from '@mui/icons-material/ArrowDownward';
import ArrowUpward from '@mui/icons-material/ArrowUpward';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import { alpha, css, type SxProps, styled, type Theme } from '@mui/material/styles';
import TableCell from '@mui/material/TableCell';
import Tooltip from '@mui/material/Tooltip';
import { SortDirection } from '@openx/types/tableCriteria.ts';
import type { BaseRow, StickyPosition, TableColumn } from '@openx/types/tableHelpers.ts';
import { STICKY_POSITION } from '@openx/types/tableHelpers.ts';
import { InfoIcon } from '@phosphor-icons/react/Info';
import { type JSX, useCallback, useMemo } from 'react';

import { ACTION_ICON_CELL } from './sections/config.ts';

const sharedArrowIconStyles = (theme: Theme) => css`
  font-size: 14px;
  margin-left: ${theme.spacing(0.5)};
`;

const StyledArrowUpward = styled(ArrowUpward)`
  ${({ theme }) => sharedArrowIconStyles(theme)}
`;

const StyledArrowDownward = styled(ArrowDownward, { shouldForwardProp: prop => prop !== 'isDisabled' })<{
  isDisabled?: boolean;
}>`
  ${({ theme }) => sharedArrowIconStyles(theme)}

  ${({ theme, isDisabled }) =>
    isDisabled &&
    css`
      color: ${theme.palette.text.disabled};
    `}
`;

const StyledHeaderCellContainerDiv = styled(Box, {
  shouldForwardProp: prop => prop !== 'cellAlign',
})<{ cellAlign?: 'inherit' | 'left' | 'center' | 'right' | 'justify' }>`
  align-items: center;
  display: flex;
  justify-content: ${({ cellAlign }) => {
    switch (cellAlign) {
      case 'left':
        return 'flex-start';
      case 'right':
        return 'flex-end';
      case 'center':
        return 'center';
      case 'justify':
        return 'space-between';
      case 'inherit':
        return 'inherit';
      default:
        return 'flex-start';
    }
  }};
`;

const StyledHeaderCellContainerDivRight = styled(Box)`
  align-items: center;
  display: flex;
  justify-content: flex-end;
  padding-right: ${({ theme }) => theme.spacing(2.5)};
`;

const StyledTableCell = styled(TableCell, {
  shouldForwardProp: prop =>
    ![
      'isSortable',
      'isSortActive',
      'sticky',
      'stickyLeftOffset',
      'stickyRightOffset',
      'isFirstNonCheckbox',
      'isLeadingStickyRight',
    ].includes(prop as string),
})<{
  isSortable: boolean;
  isSortActive: boolean;
  sticky?: StickyPosition;
  stickyLeftOffset?: number;
  stickyRightOffset?: number;
  isFirstNonCheckbox?: boolean;
  isLeadingStickyRight?: boolean;
}>`
  word-break: break-word;

  ${({ isSortable, theme }) =>
    isSortable &&
    css`
      &:hover {
        color: ${theme.palette.primary.main};
        cursor: pointer;
      }
    `}

  ${({ isSortActive, theme }) =>
    isSortActive &&
    css`
      color: ${theme.palette.primary.main};
    `}

  ${({ sticky, stickyLeftOffset, theme }) =>
    sticky === STICKY_POSITION.LEFT &&
    css`
      position: sticky;
      left: ${stickyLeftOffset || 0}px;
      z-index: 3;
      background-color: ${theme.palette.background.paper};

      [data-scrollable='true'] & {
        box-shadow: 3px 0px 4px -1px ${alpha(theme.palette.common.black, 0.08)};
      }
    `}

  ${({ sticky, stickyRightOffset, isLeadingStickyRight, theme }) =>
    sticky === STICKY_POSITION.RIGHT &&
    css`
      position: sticky;
      right: ${stickyRightOffset || 0}px;
      z-index: 3;
      background-color: ${theme.palette.background.paper};

      ${
        isLeadingStickyRight
          ? css`
            [data-scrollable='true'] & {
              box-shadow: -2px 0px 4px 0px ${alpha(theme.palette.common.black, 0.08)};
            }
          `
          : ''
      }
    `}

  ${({ isFirstNonCheckbox }) => isFirstNonCheckbox && css`padding-left: 32px;`}
`;

export interface TableHeaderCellProps<RowT extends BaseRow> {
  column: TableColumn<RowT>;
  toggleSort?: (columnKey: string) => void;
  appliedSortDirection?: SortDirection;
  overrideTableCellStyles?: SxProps<Theme>;
  overrideHeaderCellStyles?: SxProps<Theme>;
  stickyLeftOffset?: number;
  stickyRightOffset?: number;
  isFirstNonCheckbox?: boolean;
  isLeadingStickyRight?: boolean;
}

export function TableHeaderCell<RowT extends BaseRow>(props: TableHeaderCellProps<RowT>): JSX.Element {
  const {
    column,
    toggleSort,
    appliedSortDirection,
    overrideTableCellStyles,
    overrideHeaderCellStyles,
    stickyLeftOffset,
    stickyRightOffset,
    isFirstNonCheckbox,
    isLeadingStickyRight,
  } = props;
  const sortable = !!toggleSort;
  const sortActive = !!appliedSortDirection;

  const directionIconElement = useMemo(() => {
    if (!toggleSort) {
      return;
    } else if (!sortActive) {
      return <StyledArrowDownward data-test="sort-down" isDisabled />;
    }

    return appliedSortDirection === SortDirection.ASC ? (
      <StyledArrowUpward data-test="sort-up" />
    ) : (
      <StyledArrowDownward fontSize="small" data-test="sort-down" />
    );
  }, [appliedSortDirection, sortActive, toggleSort]);

  const toggleColumnSort = useCallback(() => toggleSort?.(column.key), [toggleSort, column.key]);

  const columnContent = useMemo(() => {
    if (column.tooltipMessage) {
      return (
        <StyledHeaderCellContainerDiv cellAlign={column.align}>
          {column.title}
          <Tooltip key="tooltip" title={column.tooltipMessage} placement="top">
            <IconButton
              size="small"
              data-test="tooltip-icon"
              onClick={event => {
                event.stopPropagation();
              }}
            >
              <InfoIcon />
            </IconButton>
          </Tooltip>
          {directionIconElement}
        </StyledHeaderCellContainerDiv>
      );
    }

    if (!sortable) {
      if (column.key === ACTION_ICON_CELL) {
        return <StyledHeaderCellContainerDivRight>{column.title}</StyledHeaderCellContainerDivRight>;
      }

      if (column.align) {
        return <StyledHeaderCellContainerDiv cellAlign={column.align}>{column.title}</StyledHeaderCellContainerDiv>;
      }

      return column.title;
    }

    return (
      <StyledHeaderCellContainerDiv cellAlign={column.align}>
        {column.title}
        {directionIconElement}
      </StyledHeaderCellContainerDiv>
    );
  }, [directionIconElement, column.key, column.tooltipMessage, column.title, column.align, sortable]);

  return (
    <StyledTableCell
      width={column.calcWidth ?? column.width}
      data-test="table-cell"
      onClick={toggleColumnSort}
      isSortActive={sortActive}
      isSortable={sortable}
      sticky={column.sticky}
      stickyLeftOffset={stickyLeftOffset}
      stickyRightOffset={stickyRightOffset}
      isFirstNonCheckbox={isFirstNonCheckbox}
      isLeadingStickyRight={isLeadingStickyRight}
      sx={
        [
          overrideTableCellStyles,
          overrideHeaderCellStyles,
          column.minWidth ? { minWidth: column.minWidth } : undefined,
          (column.calcWidth ?? column.width)?.endsWith('%') ? { maxWidth: 0, overflow: 'hidden' } : undefined,
        ].filter(Boolean) as SxProps<Theme>
      }
    >
      {columnContent}
    </StyledTableCell>
  );
}
